<template>
  <div>
    <v-layout wrap>
      <v-flex>
        <SelectChannel v-model="app" :channels="['facebook', 'line']" />
      </v-flex>
      <v-flex shrink>
        <v-btn :loading="exporting" class="ma-0" @click="handleExport">
          <v-icon>ios_share</v-icon>
          {{ $t('export') }}
        </v-btn>
      </v-flex>
    </v-layout>
    <DataTable
      :app="app"
      :items="items"
      :total="total"
      :loading="loading"
      :pagination.sync="pagination"
      class="mt-2"
      @view="handleView"
    />
    <template v-if="['line', 'line-broadcast'].includes(app)">
      <v-container>
        <template v-if="isLineBroadcastStatsLoading">
          <div class="text-xs-center">
            <v-progress-circular indeterminate color="primary" />
          </div>
        </template>
        <template v-else>
          <div>
            <strong>{{ $t('line_broadcast_stat') }}</strong>
            <v-premium-lock :inline="true"></v-premium-lock>
          </div>
          <small class="mt-2">{{ $t('remark_broadcast_line') }}</small>
          <div class="mt-3" style="border: 1px solid #eee; border-radius: 10px">
            <table class="table">
              <tr>
                <td>สถิติคนอ่านทั้งหมด</td>
                <td>
                  {{
                    lineBroadcastStat &&
                    lineBroadcastStat.overview &&
                    lineBroadcastStat.overview.uniqueImpression
                      ? lineBroadcastStat.overview.uniqueImpression
                      : 0
                  }}
                  user(s)
                </td>
              </tr>
              <tr>
                <td>สถิติคนคลิกทั้งหมด</td>
                <td>
                  {{
                    lineBroadcastStat &&
                    lineBroadcastStat.overview &&
                    lineBroadcastStat.overview.uniqueClick
                      ? lineBroadcastStat.overview.uniqueClick
                      : 0
                  }}
                  user(s)
                </td>
              </tr>
            </table>
          </div>
        </template>
      </v-container>
    </template>
    <UserListDialog
      :key="userListDialog.key"
      v-model="userListDialog.open"
      :broadcast="broadcast"
      :app="app"
      :error-data="selectedItem"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import XLSX from 'xlsx'
import dayjs from 'dayjs'
import SelectChannel from '@/components/view/dashboard/SelectChannel'
import DataTable from './DataTable'
import UserListDialog from './UserListDialog'
import broadcastApi from '@/api/broadcast'

export default {
  components: {
    DataTable,
    UserListDialog,
    SelectChannel
  },
  props: {
    broadcast: {
      type: Object,
      default: undefined
    }
  },
  data() {
    return {
      loading: true,
      items: [],
      total: 0,
      pagination: {
        page: 1,
        rowsPerPage: 10
      },
      selectedItem: undefined,
      userListDialog: {
        open: false,
        key: Date.now()
      },
      app: 'facebook',
      exporting: false,
      lineBroadcastStat: null,
      isLineBroadcastStatsLoading: false
    }
  },
  computed: {
    ...mapGetters(['selectedBot'])
  },
  watch: {
    pagination() {
      this.fetch()
    },
    app(value) {
      this.pagination = {
        ...this.pagination,
        page: 1
      }

      if (value === 'line') {
        this.getLineBroadcastStat()
      }
    }
  },
  methods: {
    getData(props) {
      return broadcastApi.getErrorBroadcastLogs({
        page_id: this.selectedBot.pageid,
        broadcast_id: this.broadcast.id,
        app: this.app,
        limit: this.pagination.rowsPerPage,
        offset: (this.pagination.page - 1) * this.pagination.rowsPerPage,
        ...props
      })
    },
    async fetch() {
      try {
        this.loading = true

        const response = await this.getData()
        if (response.status === 200) {
          const { data, total } = response.data
          this.items = data.map((item) => this.transformError(item))
          this.total = total
        }
      } catch (err) {
        console.error(err)
      } finally {
        this.loading = false
      }
    },
    transformError({ error, users_total }) {
      switch (this.app) {
        case 'facebook': {
          const { error: err } = JSON.parse(error)
          return {
            totalUsers: +users_total,
            type: err.type,
            code: err.code,
            subCode: err.error_subcode,
            message: err.message
          }
        }
        case 'line': {
          const { err } = JSON.parse(error)
          return {
            totalUsers: +users_total,
            message: JSON.stringify(err)
          }
        }
      }
    },
    async handleView(item) {
      this.selectedItem = item
      this.userListDialog.key = Date.now()
      await this.$nextTick()
      this.userListDialog.open = true
    },
    async handleExport() {
      try {
        this.exporting = true

        const response = await this.getData({ limit: 10000, offset: 0 })
        if (response.status === 200) {
          const { data } = response.data

          const header = (() => {
            if (this.app === 'line') {
              return ['Total users', 'Error message']
            }

            return ['Total users', 'Error type', 'Error code', 'Error sub-code', 'Error message']
          })()

          const rows = [
            [`Broadcast #${this.broadcast.id}: ${this.broadcast.name}`],
            [`ช่องทาง: ${this.app}`],
            [],
            header,
            ...data.map((item) => Object.values(this.transformError(item)))
          ]

          const worksheet = XLSX.utils.aoa_to_sheet(rows)
          const workbook = XLSX.utils.book_new()
          XLSX.utils.book_append_sheet(workbook, worksheet, 'Broadcast Error')
          XLSX.writeFile(workbook, `broadcast-error-${Date.now()}.xlsx`)
        }
      } catch (err) {
        console.error(err)
      } finally {
        this.exporting = false
      }
    },
    async getLineBroadcastStat() {
      try {
        this.isLineBroadcastStatsLoading = true

        let to = dayjs(this.broadcast.senttime).add(14, 'days').format('YYYYMMDD')
        let from = dayjs(this.broadcast.senttime).format('YYYYMMDD')
        let response = await this.get(
          `api/cms/v1/broadcast/line/statistics-per-unit?page_id=${this.selectedBot.pageid}&custom_aggregation_unit=ZWIZ_BROADCAST_${this.broadcast.id}&from=${from}&to=${to}`
        )
        if (response.status === 200) {
          this.lineBroadcastStat = response.data.result
        }
      } catch (err) {
        console.error(err)
      } finally {
        this.isLineBroadcastStatsLoading = false
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/view/broadcast/BroadcastHistory/index.vue